import Axios from "axios";
import add from "date-fns/add";
import isAfter from "date-fns/isAfter";
import isPast from "date-fns/isPast";
import isToday from "date-fns/isToday";
import { Constants } from "../@types/Constants";
import { ISort } from "../@types/ISort";
import { coreGet } from "../services/core";
import { formatDate, searchDateFormat } from "../services/dates";
import { Storage } from "./storage";
import { store } from "../redux/store";
import IURLSearchParams from "../@types/IURLSearchParams";
import queryString from "query-string";
import moment from "moment-mini";
export const isBrowser = typeof window !== "undefined";
export const convertArrayToObject = (array: Array<any>, key: string): any => {
  const initialValue = {};
  if (Array.isArray(array)) {
    return array.reduce((obj, item) => {
      return {
        ...obj,
        [item[key]]: item,
      };
    }, initialValue);
  } else {
    return initialValue;
  }
};

export const getQueryParameters = (
  queryString?: string
): {
  [k: string]: any;
} | null => {
  if (!queryString) return null;
  const urlParams: any = new URLSearchParams(queryString);
  return Object.fromEntries(urlParams);
};

export const priceFormatter = (
  price: number,
  decimals = 2,
  currencyCode = "USD"
): string | number => {
  let formattedPrice: string | null = null;
  if ((price || price === 0) && !isNaN(price)) {
    const formatter = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode || "USD",
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    formattedPrice = formatter.format(price);
  }
  return formattedPrice || price;
};

export const getCoreSessionToken = async (): Promise<string | null> => {
  let token = null;
  const restUrl = `${process.env.GATSBY_CORE_BASE_URL}/rest/session/token`;
  try {
    const response = await Axios.get(restUrl);
    token = await response.data;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
  }
  return token;
};

export const isEmptyObject = (obj: { [key: string]: string }): boolean => {
  for (const x in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, x)) return false;
  }
  return true;
};

export const formatCurrency = (num: number, decimals = 2): string => {
  return "$" + num.toFixed(decimals).replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const getLocationPlaceId = async (params: any): Promise<string> => {
  const inputStr = encodeURIComponent(params.location);
  const apiUrl = `/placeid/${params.lat},${params.lon}/${inputStr}`;

  let locationPlaceId = null;

  try {
    const cacheKey = `pc_${params.lat},${params.lon}`;
    locationPlaceId = Storage.GetLocalStorageValue(cacheKey);
    if (!locationPlaceId) {
      const response = await coreGet(apiUrl);
      locationPlaceId = response.placeid;
      Storage.SetLocalStorageValue(cacheKey, locationPlaceId);
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
  }

  return locationPlaceId;
};

export const sortRooms = (unsortedRooms: Array<ISort>): ISort[] => {
  const availableRooms = [];
  const soldoutRooms: Array<ISort> = [];
  let soldoutAccessibleRooms: Array<ISort> = [];
  unsortedRooms.forEach((r) => {
    if (!r.Availability || r.Availability < 1) {
      const rname = r.Name?.toLowerCase();
      if (
        rname &&
        (rname.indexOf("accessible") !== -1 || rname.indexOf("ada") !== -1)
      ) {
        soldoutAccessibleRooms.push(r);
      } else {
        soldoutRooms.push(r);
      }
    } else {
      availableRooms.push(r);
    }
  });
  const sortedSoldoutRooms = soldoutRooms.sort(
    (r1: { Name: string }, r2: { Name: string }) => {
      if (r1.Name < r2.Name) return -1;
      else if (r1.Name > r2.Name) return 1;
      else return 0;
    }
  );
  soldoutAccessibleRooms = soldoutAccessibleRooms.sort(
    (r1: { Name: string }, r2: { Name: string }) => {
      if (r1.Name < r2.Name) return -1;
      else if (r1.Name > r2.Name) return 1;
      else return 0;
    }
  );

  availableRooms.push(...sortedSoldoutRooms);
  availableRooms.push(...soldoutAccessibleRooms);
  return availableRooms;
};

export const gotoTab = (eleId: string, isMobileView = false): void => {
  const targetElm = document.getElementById(eleId);
  if (targetElm) {
    const elementRect = targetElm.getBoundingClientRect();
    const absoluteElementTop = elementRect.top + window.pageYOffset;
    const middle = absoluteElementTop; // - (window.innerHeight / 2);
    const adjust = isMobileView ? 270 : 120;
    setTimeout(() => {
      window.scrollTo(0, middle - adjust);
    }, 0);
    // targetElm.scrollIntoView({block: "nearest"});
  }
};

export const addHiddenIframe = (id: string, src: string): void => {
  const iframe = document.createElement("iframe");
  iframe.id = id;
  iframe.style.display = "none";
  iframe.src = src;
  document.body.appendChild(iframe);
};

export const getSearchPayloadStayDates = (
  checkinDate: Date | undefined | null,
  checkoutDate: Date | undefined | null,
  lengthOfStay: number | null,
  leadDays: number | null,
  hotelDefaultCheckinDate: string | number | null
): { formedCheckin: string | null; formedCheckout: string | null } => {
  let payloadCheckin = null;
  let payloadCheckout = null;
  if (checkinDate && checkoutDate) {
    payloadCheckin = checkinDate;
    payloadCheckout = checkoutDate;
  } else {
    if (
      !checkinDate &&
      leadDays &&
      leadDays &&
      leadDays > 0 &&
      leadDays < 365
    ) {
      const today = new Date();
      payloadCheckin = add(today, { days: leadDays });
      payloadCheckout =
        checkoutDate &&
        !isPast(checkoutDate) &&
        isAfter(checkoutDate, payloadCheckin)
          ? checkoutDate
          : add(payloadCheckin, { days: 1 });
    }
    if (
      !checkinDate &&
      hotelDefaultCheckinDate &&
      isAfter(new Date(hotelDefaultCheckinDate), new Date())
    ) {
      payloadCheckin = new Date(hotelDefaultCheckinDate);
      payloadCheckout =
        checkoutDate &&
        !isPast(checkoutDate) &&
        isAfter(checkoutDate, payloadCheckin)
          ? checkoutDate
          : add(payloadCheckin, { days: 1 });
    }
    if (
      !checkoutDate &&
      lengthOfStay &&
      lengthOfStay > 0 &&
      lengthOfStay < 365
    ) {
      payloadCheckin = checkinDate || payloadCheckin || new Date();
      payloadCheckout = add(payloadCheckin, { days: lengthOfStay });
    }
  }
  if (
    payloadCheckin &&
    payloadCheckout &&
    (!isPast(payloadCheckin) || isToday(payloadCheckin)) &&
    isAfter(payloadCheckout, payloadCheckin)
  ) {
    return {
      formedCheckin: formatDate(payloadCheckin, searchDateFormat),
      formedCheckout: formatDate(payloadCheckout, searchDateFormat),
    };
  } else {
    return {
      formedCheckin: null,
      formedCheckout: null,
    };
  }
};

export const getCRSSubSource = (): string => {
  const subSourceFromURL = sessionStorage[Constants.CRS_SUB_SOURCE];
  const subSource = subSourceFromURL
    ? subSourceFromURL
    : process.env.GATSBY_CRS_SUB_SOURCE;
  return subSource || "";
};

export const setCRSSubSourceFromURL = (
  locationSearch: string | undefined
): void => {
  const queryParams = locationSearch ? getQueryParameters(locationSearch) : {};
  if (queryParams?.subSource) {
    sessionStorage[Constants.CRS_SUB_SOURCE] = queryParams.subSource.trim();
  }
};

export const setTripadvisorRefidFromURL = (
  locationSearch: string | undefined
): void => {
  if (!process.env.GATSBY_TRIPADVISOR_DATA_SHARING_ID) return;
  const queryParams = locationSearch ? getQueryParameters(locationSearch) : {};
  if (queryParams?.refid) {
    if (typeof document === "undefined") return;
    const refid = queryParams.refid.trim();
    const expires = new Date();
    expires.setDate(expires.getDate() + 30);
    document.cookie = `${Constants.TRIPADVISOR_REFID_KEY}=${encodeURIComponent(
      refid
    )}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
  }
};

export const retrieveTripadvisorRefid = (): string | null => {
  if (!process.env.GATSBY_TRIPADVISOR_DATA_SHARING_ID) return null;
  if (typeof document === "undefined") return null;
  const cookieRow = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${Constants.TRIPADVISOR_REFID_KEY}=`));
  if (!cookieRow) return null;
  return decodeURIComponent(cookieRow.split("=").slice(1).join("=")) || null;
};

export const getLoggedStatus = (): string => {
  const state = store.getState();

  const isLoggedin = state?.member?.isLoggedIn; //Storage.GetLocalStorageValue('isLoggedIn');
  return isLoggedin ? "Logged In" : "Logged Out";
};

export const getLoggedUserInfo = (): {
  email: string | undefined;
  profileType: string | undefined;
} => {
  const state = store.getState();

  const member = state?.member; //Storage.GetLocalStorageValue('isLoggedIn');

  return {
    email: member?.profile?.email,
    profileType: member?.crmBasicProfile?.profileType,
  };
};

export const convertToWord = (stringVal: string): string => {
  return stringVal
    .split("-")
    .map((word) => word[0]?.toUpperCase() + word.substring(1))
    .join(" ");
};

export const extractBrandFromUrl = (search: string): [] => {
  let brandsArr = [];
  if (search.length) {
    const qp = queryString.parse(search, {
      parseBooleans: true,
      arrayFormat: "index",
    });
    const queryParams = Object.keys(qp).reduce((_qp, key) => {
      _qp[key.toLowerCase()] = qp[key];
      return _qp;
    }, {} as IURLSearchParams);
    if (queryParams[`brand`]) {
      brandsArr = queryParams[`brand`]
        .split("|")
        .map((str: string) => str.toLowerCase());
    }
  }
  return brandsArr;
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const trimOccupancyData = (rooms: any) => {
  return rooms.map((item: any) => {
    // Check if rate and room properties exist
    if (!item.rate || !item.room) {
      // If any of them is missing, return the original item
      return item;
    }

    // Destructure rate, room, and the remaining properties
    const { rate, room, ...rest } = item;

    // Create a trimmed version of the rate object
    const trimmedRate = { ...rate };
    // Delete unnecessary properties from the trimmed rate object
    delete trimmedRate.currencyCode;
    delete trimmedRate.description;
    delete trimmedRate.guarantee;
    delete trimmedRate.resortFee;
    delete trimmedRate.roomRateNightly;
    delete trimmedRate.taxItems;
    delete trimmedRate.sort;

    // Create a trimmed version of the room object
    const trimmedRoom = { ...room };
    // Delete unnecessary properties from the trimmed room object
    delete trimmedRoom.amenities;
    delete trimmedRoom.description;
    delete trimmedRoom.imageUrls;
    delete trimmedRoom.galleryImages;
    delete trimmedRoom.Rates;
    delete trimmedRoom.services;

    // Merge the remaining properties with the trimmed rate and room objects
    return { ...rest, rate: trimmedRate, room: trimmedRoom };
  });
};

export const formatRedemptionItem = (currency: number): string => {
  const formattedCurrency = Number(currency).toFixed();
  return Number(formattedCurrency).toLocaleString();
};

export const extractBiasFromUrl = (
  search: string
): string | string[] | null => {
  let biasValue = null;

  if (search.length) {
    const queryParams = queryString.parse(search);

    if (queryParams["bias"]) {
      biasValue = queryParams["bias"];
    }
  }

  return biasValue;
};

export const getUserTripsDataforGA = (
  recentReservations: any,
  upcomingReservations: any
) => {
  let upcomingTrip;
  let daysLeftForUpcomingTrip;
  let hotelIdOfLastStay;

  const sortedReservations = Object.entries(upcomingReservations)
    .sort(([, a], [, b]) => {
      const dateA = moment(a.arrival, "MM/DD/YYYY");
      const dateB = moment(b.arrival, "MM/DD/YYYY");
      return dateA - dateB;
    })
    .filter(([, reservation]) => {
      const arrivalDate = moment(reservation?.arrival, "MM/DD/YYYY");
      return arrivalDate.isAfter(moment());
    });

  const sortedKeys = sortedReservations.map(([key]) => key);
  let nextUpcomingReservation;
  if (Object.keys(upcomingReservations).length > 0) {
    upcomingTrip = true;
    const nextTrip = upcomingReservations[sortedKeys[0]];
    nextUpcomingReservation = nextTrip;
    const futureDate = moment(nextTrip?.arrival, "MM/DD/YYYY");
    daysLeftForUpcomingTrip = futureDate.diff(moment(), "days");
  } else {
    upcomingTrip = false;
    daysLeftForUpcomingTrip = "NA";
  }

  if (Object.keys(recentReservations).length > 0) {
    const firstKey = Object.keys(recentReservations)[0];
    const lastTrip = recentReservations[firstKey];
    hotelIdOfLastStay = lastTrip.hotelId;
  } else {
    hotelIdOfLastStay = "NA";
  }

  return {
    upcomingTrip,
    nextUpcomingReservation,
    daysLeftForUpcomingTrip,
    hotelIdOfLastStay,
  };
};
export const showNewRateCard = () => {
  if (typeof window === "undefined") return false;

  const enabled = process.env.GATSBY_SHOW_NEW_RATE_PRESENTATION === "true";
  const hidden =
    window.sessionStorage.getItem("HIDE_NEW_RATE_PRESENTATION") === "true";

  return enabled && !hidden;
};
export const showNewCheckoutExperienceDesktop = () => {
  if (typeof window === "undefined") return false;

  const enabled =
    process.env.GATSBY_SHOW_NEW_CHECKOUT_EXPERIENCE_DESKTOP === "true";
  const hidden =
    window.sessionStorage.getItem("HIDE_NEW_CHECKOUT_EXPERIENCE_DESKTOP") ===
    "true";

  return enabled && !hidden;
};

export const showNewCheckoutExperienceMobile = () => {
  if (typeof window === "undefined") return false;

  const enabled =
    process.env.GATSBY_SHOW_NEW_CHECKOUT_EXPERIENCE_MOBILE === "true";
  const hidden =
    window.sessionStorage.getItem("HIDE_NEW_CHECKOUT_EXPERIENCE_MOBILE") ===
    "true";

  return enabled && !hidden;
};

/**
 * Utility function to handle special rate selection logic for "Your Price" cards
 * when there's a mix of member and non-member rates across multiple rooms.
 *
 * @param rooms - Array of room objects
 * @param rateName - Name of the rate (e.g., "Your Price")
 * @param rateCode - Rate code to search for in room rates
 * @returns Array of selected rates for each room
 */
export const getSelectedRatesForMixedMemberRates = (
  rooms: any[],
  rateName: string,
  rateCode?: string
): any[] => {
  const roomsArray = rooms ?? [];

  // For "Your Price" cards, always use the actual room rates to preserve mixed scenarios
  if (rateName === "Your Price" && roomsArray.length > 1) {
    const hasMemberRate = roomsArray.some(
      (room: any) =>
        room.rate?.rateType === "member" || room.rate?.RateType === "member"
    );
    const hasNonMemberRate = roomsArray.some(
      (room: any) =>
        room.rate?.rateType !== "member" && room.rate?.RateType !== "member"
    );

    // For "Your Price", always use the actual selected rates from each room to maintain mixed scenarios
    const selectedRates = roomsArray.map((room: any) => room.rate);
    return selectedRates;
  }

  // For non-"Your Price" cards, use the standard logic
  const selectedRates = roomsArray.map((room: any) =>
    rateCode
      ? room.room?.Rates?.find((rt: any) => rt.rateCode === rateCode) ??
        room.rate
      : room.rate
  );

  return selectedRates;
};

export function retrieveRakutenData(): [string | null, string | null] {
  if (typeof document === "undefined") return [null, null];

  const COOKIE_NAME = "rmStoreGateway";

  const cookieRow = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${COOKIE_NAME}=`));

  if (!cookieRow) return [null, null];

  try {
    const encodedValue = cookieRow.split("=").slice(1).join("=");
    const decodedValue = decodeURIComponent(encodedValue);

    const parts = decodedValue.split("|");
    const map: Record<string, string> = {};

    for (const part of parts) {
      const [k, ...rest] = part.split(":");
      if (!k || rest.length === 0) continue;
      map[k] = rest.join(":");
    }

    const ranID = map.atrv ?? null;

    let isoTime: string | null = null;

    // Convert ald → ISO without milliseconds
    if (map.ald) {
      const ald = map.ald; // e.g. "20251127_0204"
      const yyyy = ald.slice(0, 4);
      const mm = ald.slice(4, 6);
      const dd = ald.slice(6, 8);
      const HH = ald.slice(9, 11);
      const MM = ald.slice(11, 13);

      const date = new Date(
        Date.UTC(
          Number(yyyy),
          Number(mm) - 1,
          Number(dd),
          Number(HH),
          Number(MM),
          0
        )
      );

      // Remove milliseconds
      isoTime = date.toISOString().replace(/\.\d{3}Z$/, "Z");
    }

    // Fallback: auld → ISO without milliseconds
    if (!isoTime && map.auld) {
      const unixSeconds = Number(map.auld);
      if (!isNaN(unixSeconds)) {
        isoTime = new Date(unixSeconds * 1000)
          .toISOString()
          .replace(/\.\d{3}Z$/, "Z");
      }
    }

    return [ranID, isoTime];
  } catch {
    return [null, null];
  }
}

export const formatPhoneNumber = (value: string) => {
  if (!value) return "";

  // remove all non-digits
  const digits = value.replace(/\D/g, "").slice(0, 10);

  const len = digits.length;

  if (len < 4) return digits;
  if (len < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;

  if (digits.length !== 10) {
    return value; // keep whatever user typed
  }
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export const formatPhoneNumberExtended = (value: string) => {
  if (!value) return "";

  // remove all non-digits and limit to 15 digits
  const digits = value.replace(/\D/g, "").slice(0, 15);

  const len = digits.length;

  // For US numbers (10 digits or less), format with parentheses and dashes
  if (len <= 10) {
    if (len < 4) return digits;
    if (len < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    if (len < 10)
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(
      6,
      10
    )}`;
  }

  // For international numbers (more than 10 digits), return without formatting
  return digits;
};
